import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Box, Button, IconButton, Tooltip } from "@mui/material";
import { Add, InfoOutlined, ModeEditOutlineTwoTone, AssignmentInd } from "@mui/icons-material";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import { useLazyListVendorQuotationsQuery } from "store/services/vendorQuotationApi";
import TableFilter from "components/Shared/AppTable/TableFilter";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import AppTable from "components/Shared/AppTable";
import CustomTabs from "components/Shared/CustomTabs";
import HighlightedText from "components/Shared/HighlightedText";
import { useAppMeta, useTablePagination } from "hooks";
import AssignLeadModal from "../VendorLead/vendorLeadComponents/AssignLeadModal";

const VendorQuotationListScreen = () => {
    const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
        useTablePagination();

    const [searchParams] = useSearchParams();
    const activeTab = searchParams.get("tab") || "Active";
    const keyword = searchParams.get("search") || "";

    const [assignModalOpen, setAssignModalOpen] = React.useState(false);

    const breadcrumbs = [{ title: "Vendor Quotations" }];

    const tabs = [
        { label: "Active", value: "Active" },
        { label: "Inactive", value: "Inactive" },
    ];

    const columns = [
        {
            name: "Quotation ID",
            selector: (row) => row.vendorQuotationId,
            cell: (row) => (
                <Link
                    title={row?.vendorQuotationId}
                    to={`/app/crm/vendor-quotation/${row?._id}/detail`}
                    className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
                >
                    {row?.vendorQuotationId ? (
                        <HighlightedText text={row?.vendorQuotationId} highlight={keyword} />
                    ) : (
                        "N/A"
                    )}
                </Link>
            ),
        },
        {
            name: "Title",
            selector: (row) => row.title,
            cell: (row) => (
                <span className="line-clamp-2">
                    {row?.title ? (
                        <HighlightedText text={row?.title} highlight={keyword} />
                    ) : (
                        "N/A"
                    )}
                </span>
            ),
        },
        {
            name: "Created By",
            selector: (row) => row.createdBy?.name,
            cell: (row) => row?.createdBy?.name || "N/A",
        },
        {
            name: "Created At",
            selector: (row) => row.createdAt,
            cell: (row) => <AppTableDateTime date={row?.createdAt} />,
        },
        {
            name: "Actions",
            cell: (row) => (
                <>
                    <Link to={`/app/crm/vendor-quotation/${row._id}/detail`}>
                        <IconButton size="small" aria-label="View Vendor Quotation" color="primary">
                            <InfoOutlined />
                        </IconButton>
                    </Link>
                    <Link to={`/app/crm/vendor-quotation/${row._id}/edit`}>
                        <IconButton size="small" aria-label="Edit Vendor Quotation">
                            <ModeEditOutlineTwoTone />
                        </IconButton>
                    </Link>
                </>
            ),
            ignoreRowClick: true,
            right: true,
        },
    ];

    const [
        fetchVendorQuotations,
        { data: vendorQuotationRes, isLoading, isFetching, error },
    ] = useLazyListVendorQuotationsQuery();

    React.useEffect(() => {
        fetchVendorQuotations({
            active: activeTab === "Active",
            page: currentPage,
            limit: perPage,
            ...(keyword && { keyword }),
        });
    }, [activeTab, currentPage, fetchVendorQuotations, keyword, perPage]);

    useAppMeta(`${activeTab} Vendor Quotations`);

    const PageActions = () => (
        <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title="Assign Lead">
                <Button
                    startIcon={<AssignmentInd />}
                    variant="outlined"
                    color="secondary"
                    onClick={() => setAssignModalOpen(true)}
                >
                    Assign Lead
                </Button>
            </Tooltip>
            <Link to="/app/crm/vendor-quotation/create">
                <Button startIcon={<Add />} variant="contained">
                    New Vendor Quotation
                </Button>
            </Link>
        </Box>
    );

    return (
        <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
            <BreadcrumbLayout.Error error={error} />
            <BreadcrumbLayout.Paper>
                <CustomTabs tabs={tabs} activeTab={activeTab} />
                <TableFilter
                    searchLabel="Search Vendor Quotations"
                    disabled={isLoading || isFetching}
                />
                <AppTable
                    columns={columns}
                    data={vendorQuotationRes?.data ?? []}
                    progressPending={isLoading || isFetching}
                    paginationServer
                    paginationPerPage={perPage}
                    paginationTotalRows={vendorQuotationRes?.totalItems ?? 0}
                    paginationDefaultPage={currentPage}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerRowsChange}
                />
            </BreadcrumbLayout.Paper>

            <AssignLeadModal
                open={assignModalOpen}
                onClose={() => setAssignModalOpen(false)}
                type="pdf"
            />
        </BreadcrumbLayout>
    );
};

export default VendorQuotationListScreen;